import {useCallback, useMemo, useState} from "react";

export const diffDashboardChanges = (
  saved: ReadonlyArray<string>,
  draft: ReadonlyArray<string>,
): {grantedIds: ReadonlyArray<string>; revokedIds: ReadonlyArray<string>} => {
  const savedSet = new Set(saved);
  const draftSet = new Set(draft);

  return {
    grantedIds: draft.filter(id => !savedSet.has(id)),
    revokedIds: saved.filter(id => !draftSet.has(id)),
  };
};

export const countDashboardChanges = (
  saved: ReadonlyArray<string>,
  draft: ReadonlyArray<string>,
): number => {
  const {grantedIds, revokedIds} = diffDashboardChanges(saved, draft);

  return grantedIds.length + revokedIds.length;
};

type PersistDashboardChanges = (params: {
  email: string;
  dashboardIds: ReadonlyArray<string>;
  selected: boolean;
}) => Promise<unknown>;

type Params = Readonly<{
  email: string;
  savedCheckedIds: ReadonlyArray<string>;
  persistDashboardChanges: PersistDashboardChanges;
  onPersistSuccess: () => void;
}>;

export const useQuicksightDashboardDraft = ({
  email,
  savedCheckedIds,
  persistDashboardChanges,
  onPersistSuccess,
}: Params) => {
  const [draftCheckedIds, setDraftCheckedIds] =
    useState<ReadonlyArray<string> | null>(null);

  const checkedValues = draftCheckedIds ?? savedCheckedIds;

  const pendingChangeCount = useMemo(
    () =>
      draftCheckedIds
        ? countDashboardChanges(savedCheckedIds, draftCheckedIds)
        : 0,
    [draftCheckedIds, savedCheckedIds],
  );

  const setDashboardChecked = useCallback(
    (dashboardId: string, checked: boolean) => {
      setDraftCheckedIds(current => {
        const base = current ?? savedCheckedIds;

        if (checked) {
          return base.includes(dashboardId) ? base : [...base, dashboardId];
        }

        return base.filter(id => id !== dashboardId);
      });
    },
    [savedCheckedIds],
  );

  const setGroupChecked = useCallback(
    (dashboardIds: ReadonlyArray<string>, checked: boolean) => {
      setDraftCheckedIds(current => {
        const base = current ?? savedCheckedIds;

        if (checked) {
          const next = new Set(base);

          dashboardIds.forEach(id => next.add(id));

          return [...next];
        }

        const idsToRemove = new Set(dashboardIds);

        return base.filter(id => !idsToRemove.has(id));
      });
    },
    [savedCheckedIds],
  );

  const discard = useCallback(() => {
    setDraftCheckedIds(null);
  }, []);

  const save = useCallback(async () => {
    if (!draftCheckedIds || pendingChangeCount === 0) {
      return;
    }

    const {grantedIds, revokedIds} = diffDashboardChanges(
      savedCheckedIds,
      draftCheckedIds,
    );

    if (grantedIds.length > 0) {
      await persistDashboardChanges({
        email,
        dashboardIds: grantedIds,
        selected: true,
      });
    }

    if (revokedIds.length > 0) {
      await persistDashboardChanges({
        email,
        dashboardIds: revokedIds,
        selected: false,
      });
    }

    setDraftCheckedIds(null);
    onPersistSuccess();
  }, [
    draftCheckedIds,
    email,
    onPersistSuccess,
    pendingChangeCount,
    persistDashboardChanges,
    savedCheckedIds,
  ]);

  return {
    checkedValues,
    discard,
    pendingChangeCount,
    save,
    setDashboardChecked,
    setGroupChecked,
  };
};

/**
 * Lifted to the user-detail page when `deferSave` is on: footer Save/Discard and
 * header permission totals both consume this snapshot.
 */
export type QuicksightDashboardDeferredSaveHandlers = Readonly<{
  discard: () => void;
  pendingChangeCount: number;
  save: () => Promise<void>;
  /** See `isQuicksightHeaderStatsReady`. */
  statsReady: boolean;
  /** Checked dashboards in the current draft (or saved) selection. */
  selectedCount: number;
  /** All dashboards returned for the org/user list. */
  totalCount: number;
}>;
